/**
 * TITAN — Ant Design 5 theme (BLUE accent)
 * Drop-in ConfigProvider theme — identical to titan-antd-theme.ts but with the
 * Blue accent (#2563eb) baked in as the primary brand color.
 *
 *   import { ConfigProvider } from 'antd';
 *   import { titanTheme } from './titan-antd-theme-blue';
 *
 *   <ConfigProvider theme={titanTheme}>
 *     <App />
 *   </ConfigProvider>
 *
 * Works as plain JS or TS. Token names follow AntD 5 (antd >= 5.12).
 */

/* ---- raw palette (source of truth) ---- */
export const palette = {
  // brand / accent (Blue)
  accent: '#2563eb',
  accentHover: '#1d4ed8',
  accentActive: '#1e40af',
  accentSoft: '#e7eefe',
  accentLine: '#bcd0fb',

  // navy (sidebar) — not an AntD token, use directly in your layout
  navy: '#0e1a36',
  navyLine: '#1c2a4d',
  navActive: '#3b82f6',

  // surfaces / lines
  canvas: '#f4f5f8', // page background
  card: '#ffffff',
  line: '#e6e8ee', // dividers / borders
  line2: '#eef0f4', // subtle row dividers
  fieldLine: '#d7dbe3', // input border
  fieldLineHover: '#b6bccb',

  // text
  ink: '#0f1729', // primary / headings
  ink2: '#475067', // labels / secondary
  ink3: '#7b8499', // tertiary
  ink4: '#9aa2b4', // placeholder / disabled
  req: '#e0506a', // required marker

  // status
  blue: '#2956c9',
  blueBg: '#e7eefe',
  green: '#178a5a',
  greenBg: '#e3f6ec',
  red: '#c33a52',
  redBg: '#fdeaec',
  amber: '#b06a16',
  amberBg: '#fff5e8',
  violet: '#6d28d9',
  violetBg: '#f1ebfe',
};

const fontStack =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/* ---- the theme ---- */
export const titanBlueTheme = {
  token: {
    colorPrimary: palette.accent,
    colorInfo: palette.accent,
    colorSuccess: palette.green,
    colorWarning: palette.amber,
    colorError: palette.red,

    colorText: palette.ink,
    colorTextHeading: palette.ink,
    colorTextSecondary: palette.ink2,
    colorTextTertiary: palette.ink3,
    colorTextQuaternary: palette.ink4,
    colorTextPlaceholder: palette.ink4,

    colorBorder: palette.fieldLine,
    colorBorderSecondary: palette.line,
    colorSplit: palette.line,

    colorBgLayout: palette.canvas,
    colorBgContainer: palette.card,
    colorBgElevated: palette.card,

    borderRadius: 6, // inputs, buttons
    borderRadiusLG: 10, // cards, modals, panels
    borderRadiusSM: 6,

    controlHeight: 38, // "Comfortable" density (use 32 for Compact)

    fontFamily: fontStack,
    fontSize: 14,
    lineHeight: 1.45,

    // soft, low-contrast elevation
    boxShadow: '0 1px 2px rgba(16,23,41,.04), 0 1px 3px rgba(16,23,41,.03)',
    boxShadowSecondary:
      '0 10px 30px rgba(16,23,41,.10), 0 4px 10px rgba(16,23,41,.06)',

    wireframe: false,

    // Link colors
    colorLink: palette.accent,
    colorLinkHover: palette.accentHover,
    colorLinkActive: palette.accentActive,

    // Disabled / spotlight
    colorTextDisabled: palette.ink3,
    colorBgSpotlight: palette.canvas,

    // Spacing
    padding: 16,
    paddingLG: 24,
    paddingSM: 12,
    paddingXS: 8,

    // Control heights
    controlHeightLG: 44,
    controlHeightSM: 24,
  },

  components: {
    Button: {
      controlHeight: 36,
      borderRadius: 6,
      fontWeight: 600,
      primaryShadow: '0 1px 2px rgba(37,99,235,.30)',
      defaultColor: palette.ink2,
      defaultBorderColor: palette.fieldLine,
      defaultBg: palette.card,
    },
    Input: {
      controlHeight: 38,
      borderRadius: 6,
      colorBorder: palette.fieldLine,
      hoverBorderColor: palette.fieldLineHover,
      activeBorderColor: palette.accent,
      activeShadow: '0 0 0 3px ' + palette.accentSoft,
      paddingInline: 12,
    },
    InputNumber: {
      controlHeight: 38,
      borderRadius: 6,
      hoverBorderColor: palette.fieldLineHover,
      activeBorderColor: palette.accent,
      activeShadow: '0 0 0 3px ' + palette.accentSoft,
    },
    Select: {
      controlHeight: 38,
      borderRadius: 6,
      colorBorder: palette.fieldLine,
      optionSelectedBg: palette.accentSoft,
      optionSelectedColor: palette.accent,
      optionSelectedFontWeight: 600,
      // multi-select tags
      multipleItemBg: palette.accentSoft,
      multipleItemBorderColor: palette.accentLine,
      // disabled multi-select tags
      multipleItemBgDisabled: palette.line2,
      multipleItemColorDisabled: palette.ink3,
    },
    DatePicker: {
      controlHeight: 38,
      borderRadius: 6,
      activeBorderColor: palette.accent,
      activeShadow: '0 0 0 3px ' + palette.accentSoft,
    },
    Table: {
      headerBg: palette.card, // white header (set palette.canvas for grey variant)
      headerColor: palette.ink3,
      headerSplitColor: 'transparent',
      borderColor: palette.line2,
      rowHoverBg: palette.accentSoft,
      cellPaddingBlock: 16,
      cellPaddingInline: 20,
      headerBorderRadius: 10,
      fontWeightStrong: 600,
    },
    Card: {
      borderRadiusLG: 10,
      paddingLG: 24,
      colorBorderSecondary: palette.line,
      headerFontSize: 14,
      boxShadowTertiary:
        '0 1px 2px rgba(16,23,41,.04), 0 1px 3px rgba(16,23,41,.03)',
    },
    Modal: {
      borderRadiusLG: 12,
      titleFontSize: 16,
      headerBg: palette.card,
      contentBg: palette.card,
      paddingContentHorizontalLG: 24,
    },
    Tabs: {
      inkBarColor: palette.accent,
      itemColor: palette.ink3,
      itemHoverColor: palette.ink,
      itemActiveColor: palette.accent,
      itemSelectedColor: palette.accent,
      titleFontSize: 13.5,
      horizontalItemGutter: 24,
    },
    Tag: {
      borderRadiusSM: 6,
      defaultBg: palette.line2,
      defaultColor: palette.ink2,

      // Bypass antd's algorithmic palette derivation — use the exact
      // status colors from `palette`/`statusTag` instead of a generated ramp.
      colorSuccess: palette.green,
      colorSuccessBg: palette.greenBg,
      colorSuccessBorder: palette.green,

      colorWarning: palette.amber,
      colorWarningBg: palette.amberBg,
      colorWarningBorder: palette.amber,

      colorError: palette.red,
      colorErrorBg: palette.redBg,
      colorErrorBorder: palette.red,
    },
    Form: {
      labelColor: palette.ink2,
      labelFontSize: 13,
      labelRequiredMarkColor: palette.req,
      verticalLabelPadding: '0 0 6px',
      itemMarginBottom: 18,
    },
    Menu: {
      // sidebar (dark navy)
      darkItemBg: palette.navy,
      // match darkItemBg so an open submenu doesn't render a darker "box"
      // inside the sidebar's own margin (default AntD auto-darkens this)
      darkSubMenuItemBg: palette.navy,
      darkItemSelectedBg: palette.navActive,
      darkItemColor: '#aab3cd',
      darkItemHoverColor: '#e7ebf6',
      itemBorderRadius: 8,
    },
    Pagination: {
      itemActiveBg: palette.accentSoft,
      colorPrimary: palette.accent,
    },
    Checkbox: { colorPrimary: palette.accent, borderRadiusSM: 4 },
    Radio: { colorPrimary: palette.accent },
    Switch: { colorPrimary: palette.accent },
    Layout: {
      siderBg: palette.navy,
      headerBg: palette.card,
      bodyBg: palette.canvas,
    },
    Badge: {
      colorPrimary: palette.accent,
    },
    Tooltip: {
      colorBgSpotlight: palette.navy,
      colorTextLightSolid: '#ffffff',
    },
  },
};

/**
 * Status → Tag colors. AntD <Tag> presets don't match the design exactly,
 * so use custom color/bg. Example:
 *   const s = statusTag[record.status];
 *   <Tag style={{ color: s.color, background: s.bg, border: 'none',
 *                 borderRadius: 6, fontWeight: 600, textTransform: 'capitalize' }}>
 *     {record.status}
 *   </Tag>
 */
export const statusTag = {
  submitted: { color: palette.blue, bg: palette.blueBg },
  review: { color: palette.violet, bg: palette.violetBg },
  approved: { color: palette.green, bg: palette.greenBg },
  draft: { color: palette.amber, bg: palette.amberBg },
  rejected: { color: palette.red, bg: palette.redBg },
};

/**
 * Payment status → Tag colors (Paid / Not paid).
 */
export const paymentTag = {
  paid: { color: palette.green, bg: palette.greenBg, label: 'Paid' },
  unpaid: { color: palette.red, bg: palette.redBg, label: 'Not paid' },
};

/**
 * Density presets — spread into token to switch Comfortable/Compact:
 *   token: { ...titanTheme.token, ...density.Compact }
 */
export const density = {
  Comfortable: { controlHeight: 38 },
  Compact: { controlHeight: 32 },
};

export default titanBlueTheme;
